import IntelligentContentParser from './IntelligentContentParser';

/**
 * SmartPasteAggregator - Group and deduplicate "extras" for Smart Paste UI
 *
 * @date_of_change 2025-12-09 - Initial implementation to support clearer extras review
 */

class SmartPasteAggregator {
  static normalizeValue(value) {
    return IntelligentContentParser.normalizeParsedEntryName(value);
  }

  static formatSourceSection(value) {
    if (!value) return null;
    const normalized = String(value)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_/]+/g, ' ')
      .trim();
    if (!normalized) return null;
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  static getCanonicalCatalog(data = {}) {
    if (Array.isArray(data.parsedEntryCatalog) && data.parsedEntryCatalog.length > 0) {
      return data.parsedEntryCatalog;
    }
    return IntelligentContentParser.buildCharacterEntryCatalog(data);
  }

  static buildCatalogIndex(catalog = []) {
    const byName = new Map();
    const byFingerprint = new Map();
    const kindCounts = {};

    catalog.forEach((entry, index) => {
      if (!entry) return;

      const normalizedName = this.normalizeValue(entry.name);
      const fingerprint = entry.fingerprint || IntelligentContentParser.buildParsedEntryFingerprint(entry, entry.kind);
      const normalizedKind = this.normalizeValue(entry.kind);
      const normalizedSourceSection = this.normalizeValue(entry?.parserMeta?.sourceSection || entry.kind);
      const indexedEntry = {
        ...entry,
        id: entry.id || `${entry.kind || 'entry'}:${normalizedName || index}:${index}`,
        fingerprint,
        normalizedName,
        normalizedKind,
        normalizedSourceSection
      };

      if (normalizedName) {
        if (!byName.has(normalizedName)) byName.set(normalizedName, []);
        byName.get(normalizedName).push(indexedEntry);
      }

      if (fingerprint) {
        if (!byFingerprint.has(fingerprint)) byFingerprint.set(fingerprint, []);
        byFingerprint.get(fingerprint).push(indexedEntry);
      }

      kindCounts[normalizedKind] = (kindCounts[normalizedKind] || 0) + 1;
    });

    return { byName, byFingerprint, kindCounts, entries: catalog };
  }

  static getCatalogKindsForSuggestedType(suggestedType) {
    switch (suggestedType) {
      case 'equipment':
        return ['equipment'];
      case 'actions':
        return ['actions', 'bonusActions', 'reactions'];
      case 'features':
        return ['features', 'classFeatures', 'feats', 'speciesTraits'];
      case 'traits':
        return ['speciesTraits', 'features', 'classFeatures'];
      case 'notes':
      default:
        return [];
    }
  }

  static findRelatedCatalogEntries(base, suggestedType, catalogIndex) {
    const name = this.normalizeValue(base?.name || '');
    const description = this.normalizeValue(base?.description || base?.rawLine || '');
    const fingerprint = IntelligentContentParser.buildParsedEntryFingerprint(base, base?.parserMeta?.sourceSection || suggestedType);
    const preferredKinds = new Set(this.getCatalogKindsForSuggestedType(suggestedType).map(kind => this.normalizeValue(kind)));
    const sourceSection = this.normalizeValue(base?.parserMeta?.sourceSection || suggestedType);
    const nameTokens = name.split(' ').filter(token => token.length >= 4);
    const matches = [];

    const seenIds = new Set();
    catalogIndex.entries.forEach((entry, index) => {
      if (!entry) return;

      const normalizedName = this.normalizeValue(entry.name);
      const normalizedDescription = this.normalizeValue(entry.description || '');
      const normalizedKind = this.normalizeValue(entry.kind);
      const normalizedSourceSection = this.normalizeValue(entry?.parserMeta?.sourceSection || entry.kind);

      let score = 0;
      let matchType = 'related';

      if (fingerprint && (entry.fingerprint || '') === fingerprint) {
        score += 100;
        matchType = 'exact';
      }

      if (name && normalizedName) {
        if (normalizedName === name) {
          score += 80;
          if (matchType !== 'exact') matchType = 'sameName';
        } else if (normalizedName.includes(name) || name.includes(normalizedName)) {
          score += 48;
        }
      }

      if (description && normalizedDescription) {
        if (normalizedDescription === description) {
          score += 25;
        } else if (normalizedDescription.includes(description) || description.includes(normalizedDescription)) {
          score += 15;
        }
      }

      if (nameTokens.length > 0 && normalizedName) {
        const overlap = nameTokens.filter(token => normalizedName.includes(token)).length;
        if (overlap > 0) {
          score += Math.min(30, overlap * 12);
        }
      }

      if (preferredKinds.has(normalizedKind)) {
        score += 15;
      }

      if (sourceSection && normalizedSourceSection === sourceSection) {
        score += 8;
      }

      if (score < 30) return;

      const matchId = entry.id || `${normalizedKind || 'entry'}:${normalizedName || index}:${index}`;
      if (seenIds.has(matchId)) return;
      seenIds.add(matchId);

      matches.push({
        id: matchId,
        kind: entry.kind,
        name: entry.name,
        description: entry.description || '',
        sourceLabel: this.formatSourceSection(entry?.parserMeta?.sourceSection || entry.kind),
        matchType,
        score
      });
    });

    return matches
      .sort((left, right) => right.score - left.score)
      .slice(0, 3);
  }

  static isDuplicateAgainstCatalog(base, suggestedType, catalogIndex) {
    if (suggestedType === 'notes') return false;

    const normalizedName = this.normalizeValue(base?.name || '');
    if (!normalizedName) return false;

    const normalizedDescription = this.normalizeValue(base?.description || base?.rawLine || '');
    const fingerprint = IntelligentContentParser.buildParsedEntryFingerprint(base, base?.parserMeta?.sourceSection || suggestedType);
    const exactMatches = (catalogIndex.byFingerprint.get(fingerprint) || []);
    if (exactMatches.length > 0) return true;

    const relevantKinds = new Set(this.getCatalogKindsForSuggestedType(suggestedType).map(kind => this.normalizeValue(kind)));
    const nameMatches = (catalogIndex.byName.get(normalizedName) || [])
      .filter(entry => relevantKinds.size === 0 || relevantKinds.has(entry.normalizedKind));

    if (nameMatches.length === 0) return false;

    if (!normalizedDescription) return true;

    return nameMatches.some((entry) => {
      const entryDescription = this.normalizeValue(entry.description || '');
      return !entryDescription || entryDescription === normalizedDescription;
    });
  }

  static escapeRegex(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  static extractStructuredScalar(text, fieldNames = []) {
    const sourceText = String(text || '');
    if (!sourceText.trim()) return '';

    for (const fieldName of fieldNames) {
      const escaped = this.escapeRegex(fieldName);
      const quotedDouble = sourceText.match(new RegExp(`["']?${escaped}["']?\\s*:\\s*"([^"]+)"`, 'i'));
      if (quotedDouble?.[1]) return quotedDouble[1].trim();

      const quotedSingle = sourceText.match(new RegExp(`["']?${escaped}["']?\\s*:\\s*'([^']+)'`, 'i'));
      if (quotedSingle?.[1]) return quotedSingle[1].trim();

      const bare = sourceText.match(new RegExp(`["']?${escaped}["']?\\s*:\\s*([^,\\n\\r}]+)`, 'i'));
      if (bare?.[1]) {
        const candidate = bare[1].trim().replace(/^["']|["']$/g, '');
        if (candidate) return candidate;
      }
    }

    return '';
  }

  static hasStructuredSpellData(data = {}) {
    const spellData = data?.spells || {};
    const byLevel = spellData?.byLevel && typeof spellData.byLevel === 'object'
      ? Object.values(spellData.byLevel).some((entries) => Array.isArray(entries) && entries.length > 0)
      : false;
    const prepared = Array.isArray(spellData?.prepared) && spellData.prepared.length > 0;
    const known = Array.isArray(spellData?.known) && spellData.known.length > 0;
    const legacyLevels = Array.from({ length: 10 }, (_, level) => level)
      .some((level) => Array.isArray(spellData?.[`level${level}`]) && spellData[`level${level}`].length > 0);
    return byLevel || prepared || known || legacyLevels;
  }

  static looksLikeStructuredDuplicateBlock(base, data = {}) {
    if (!base || (!base.isRaw && !base.isSectionFragment)) return false;

    const sourceText = [
      base?.name,
      base?.description,
      base?.parserMeta?.rawText
    ].filter(Boolean).join('\n');
    const normalizedText = String(sourceText || '').toLowerCase();
    if (!normalizedText.trim()) return false;

    const equipmentEntries = [
      ...(Array.isArray(data?.equipment?.weapons) ? data.equipment.weapons : []),
      ...(Array.isArray(data?.equipment?.armor) ? data.equipment.armor : []),
      ...(Array.isArray(data?.equipment?.items) ? data.equipment.items : []),
      ...(Array.isArray(data?.equipment?.valuables) ? data.equipment.valuables : [])
    ];
    const hasFeatureData =
      (Array.isArray(data?.features) && data.features.length > 0) ||
      (Array.isArray(data?.classFeatures) && data.classFeatures.length > 0) ||
      (Array.isArray(data?.feats) && data.feats.length > 0) ||
      (Array.isArray(data?.speciesTraits) && data.speciesTraits.length > 0);
    const hasIdentityData = Boolean(data?.name || data?.class || data?.race || data?.species || data?.background || data?.alignment);
    const hasAbilityData = Boolean(data?.abilities && Object.keys(data.abilities).length >= 4);
    const hasCombatData = Boolean(data?.ac || data?.hp || data?.maxHp || data?.speed);
    const hasBiographyData = Boolean(data?.biography && Object.keys(data.biography).length > 0);
    const hasProficiencyData = Boolean(
      (data?.proficiencies && (
        (Array.isArray(data.proficiencies.armor) && data.proficiencies.armor.length > 0) ||
        (Array.isArray(data.proficiencies.weapons) && data.proficiencies.weapons.length > 0) ||
        (Array.isArray(data.proficiencies.tools) && data.proficiencies.tools.length > 0)
      )) ||
      (Array.isArray(data?.languages) && data.languages.length > 0)
    );

    let matchedSignals = 0;

    if (hasIdentityData && /\b(name|class|classes|race|species|background|alignment)\b/.test(normalizedText)) matchedSignals += 1;
    if (hasAbilityData && /\b(strength|dexterity|constitution|intelligence|wisdom|charisma|abilities)\b/.test(normalizedText)) matchedSignals += 1;
    if (hasCombatData && /\b(hitpoints|hit points|hp|armorclass|armor class|combatstats|combat stats|initiative|speed|movement)\b/.test(normalizedText)) matchedSignals += 1;
    if (this.hasStructuredSpellData(data) && /\b(spells|spellcasting|cantrips|castingtimes?|casting time|duration|school|components)\b/.test(normalizedText)) matchedSignals += 1;
    if (equipmentEntries.length > 0 && /\b(equipment|inventory|weapon|armor|attunement|quantity|damage|damagetype|properties|scroll|potion|currency|gold|silver|platinum)\b/.test(normalizedText)) matchedSignals += 1;
    if (hasFeatureData && /\b(features?|feats?|traits?|spellcasting|arcane recovery|source|uses|maxuses|reseton)\b/.test(normalizedText)) matchedSignals += 1;
    if (hasBiographyData && /\b(appearance|backstory|personality|ideals|bonds|flaws|biography|description)\b/.test(normalizedText)) matchedSignals += 1;
    if (hasProficiencyData && /\b(proficiencies|languages|armor|weapons|tools)\b/.test(normalizedText)) matchedSignals += 1;

    const structuredKeyCount = (
      normalizedText.match(/"(?:name|class|level|race|species|background|alignment|abilities|hitpoints|savingthrows|skills|combatstats|spellcasting|spells|equipment|features|biography|appearance|backstory|proficiencies|languages|currency)"/g) || []
    ).length;

    return matchedSignals >= 3 || (matchedSignals >= 2 && structuredKeyCount >= 6);
  }

  /**
   * Build a normalized extras model from parsed content.
   * This is intentionally conservative: it only deduplicates when names match
   * clearly against already-parsed canonical lists.
   */
  static buildExtrasModel(parsedContent) {
    if (!parsedContent || !parsedContent.data) {
      return { items: [], byCategory: {}, stats: { totalOriginal: 0, totalAfterDedup: 0 } };
    }

    const { data } = parsedContent;
    const uncategorized = data.uncategorized || {};
    const canonicalCatalog = this.getCanonicalCatalog(data);
    const catalogIndex = this.buildCatalogIndex(canonicalCatalog);
    // date_of_change: 2025-12-12
    // rule_reference: Include potentialActions for combat attacks (Bite, Claw, etc.)
    const {
      potentialFeatures = [],
      potentialTraits = [],
      potentialEquipment = [],
      potentialActions = [],
      sectionFragments = [],
      rawTextBlocks = []
    } = uncategorized;

    const totalOriginal =
      potentialFeatures.length +
      potentialTraits.length +
      potentialEquipment.length +
      potentialActions.length +
      sectionFragments.length +
      rawTextBlocks.length;

    if (totalOriginal === 0) {
      return { items: [], byCategory: {}, stats: { totalOriginal: 0, totalAfterDedup: 0 } };
    }

    // @date_of_change 2025-12-09 - Treat some short labels as structural when already parsed
    const isStructuralAndAlreadyParsed = (normName) => {
      if (!normName) return false;

      // @date_of_change 2025-12-09 - Treat these as purely structural labels for extras purposes.
      // The underlying parsers already handle them (or should), and even if they miss,
      // showing them as separate "extras" is mostly noise.

      // Age headers like "Age" or "Age immortal, looks about 24"
      if (normName === 'age' || normName.startsWith('age ')) {
        return true;
      }

      // Saving throws lines like "Saving Throws: CON (+11), INT (+12)" etc.
      if (normName === 'saving throws' || normName === 'saving throw' || normName.startsWith('saving throws ')) {
        return true;
      }

      // Magic items section headers ("Magic Items", "Magic Items (Limits 8 attunements)" etc.)
      if (normName === 'magic items' || normName.startsWith('magic items')) {
        return true;
      }

      return false;
    };

    const abilityBundleConfigs = [
      { label: 'Strength', aliases: ['strength', 'str'] },
      { label: 'Dexterity', aliases: ['dexterity', 'dex'] },
      { label: 'Constitution', aliases: ['constitution', 'con'] },
      { label: 'Intelligence', aliases: ['intelligence', 'int'] },
      { label: 'Wisdom', aliases: ['wisdom', 'wis'] },
      { label: 'Charisma', aliases: ['charisma', 'cha'] }
    ];

    const skillNames = [
      'Athletics',
      'Acrobatics',
      'Sleight of Hand',
      'Stealth',
      'Arcana',
      'History',
      'Investigation',
      'Nature',
      'Religion',
      'Animal Handling',
      'Insight',
      'Medicine',
      'Perception',
      'Survival',
      'Deception',
      'Intimidation',
      'Performance',
      'Persuasion'
    ];

    const inferReviewPresentation = (name, description, suggestedType) => {
      const rawName = String(name || '').trim();
      const rawDescription = String(description || '').trim();
      const normalizedName = this.normalizeValue(rawName);
      const combined = `${rawName}\n${rawDescription}`.trim().toLowerCase();
      const extraTags = new Set();
      let friendlyName = rawName || 'Unplaced details';
      let nextSuggestedType = suggestedType;
      let suggestedReason = null;
      const structuredName = this.extractStructuredScalar(rawDescription, ['name', 'title']);
      const structuredType = this.extractStructuredScalar(rawDescription, ['type']);
      const structuredSchool = this.extractStructuredScalar(rawDescription, ['school']);
      const structuredCastingTime = this.extractStructuredScalar(rawDescription, ['castingTime', 'casting time']);
      const structuredRange = this.extractStructuredScalar(rawDescription, ['range']);
      const structuredDuration = this.extractStructuredScalar(rawDescription, ['duration']);
      const structuredQuantity = this.extractStructuredScalar(rawDescription, ['quantity']);
      const structuredDamage = this.extractStructuredScalar(rawDescription, ['damage', 'damageFormula']);
      const structuredArmorClass = this.extractStructuredScalar(rawDescription, ['armorClass', 'armor class']);
      const structuredSource = this.extractStructuredScalar(rawDescription, ['source']);
      const structuredUses = this.extractStructuredScalar(rawDescription, ['uses', 'maxUses', 'resetOn']);
      const looksLikeSpellBlock = Boolean(
        structuredName && (
          structuredSchool ||
          structuredCastingTime ||
          structuredDuration ||
          structuredRange ||
          /\bspell\b/.test(combined) ||
          structuredType === 'spell'
        )
      );
      const looksLikeEquipmentBlock = Boolean(
        structuredName && (
          structuredQuantity ||
          structuredDamage ||
          structuredArmorClass ||
          /\b(attunement|equipped|properties|weapon|armor|item|scroll|potion|pack|focus)\b/.test(combined) ||
          ['weapon', 'armor', 'item', 'consumable'].includes(this.normalizeValue(structuredType))
        )
      );
      const looksLikeFeatureBlock = Boolean(
        structuredName && !looksLikeSpellBlock && !looksLikeEquipmentBlock && (
          structuredSource ||
          structuredUses ||
          /\b(feature|ability|feat|trait|source|reseton|maxuses)\b/.test(combined)
        )
      );
      const looksLikeBiographyBlock = /\b(backstory|appearance|personality|ideals|bonds|flaws|description)\b/.test(combined);

      const detectedAbilities = abilityBundleConfigs.filter(({ aliases }) => aliases.some((alias) => {
        const abilityMentioned = new RegExp(`\\b${alias}\\b`, 'i').test(combined);
        const scoreMentioned = new RegExp(`\\b${alias}\\b[^\\n\\r]{0,18}\\b\\d{1,2}\\b`, 'i').test(combined);
        return abilityMentioned && scoreMentioned;
      }));
      const detectedSkills = skillNames.filter((skill) => combined.includes(skill.toLowerCase()));
      const hasAbilityChecks = /ability checks?/.test(combined);
      const hasSavingThrows = /saving throws?/.test(combined);
      const hasSkillSignals = detectedSkills.length >= 2 || /\bskills?\b/.test(combined);

      if (looksLikeSpellBlock) {
        friendlyName = structuredName || friendlyName;
        nextSuggestedType = 'spells';
        extraTags.add('spells');
        suggestedReason = 'Recognized a spell block from structured spell fields in the pasted text.';
      } else if (looksLikeEquipmentBlock) {
        friendlyName = structuredName || friendlyName;
        nextSuggestedType = 'equipment';
        extraTags.add('equipment');
        suggestedReason = 'Recognized an equipment item from structured item fields in the pasted text.';
      } else if (looksLikeFeatureBlock) {
        friendlyName = structuredName || friendlyName;
        nextSuggestedType = nextSuggestedType || 'features';
        extraTags.add(nextSuggestedType);
        suggestedReason = 'Recognized a feature-style block from structured source and usage details.';
      } else if (looksLikeBiographyBlock && (!rawName || normalizedName === 'untitled block' || normalizedName === 'unplaced details')) {
        friendlyName = 'Biography details';
        nextSuggestedType = 'notes';
        extraTags.add('notes');
        suggestedReason = 'Recognized biography or appearance details that may belong in character notes.';
      } else if (/\bcantrips known\b/.test(combined)) {
        friendlyName = 'Cantrips known';
        nextSuggestedType = 'spells';
        extraTags.add('spells');
        suggestedReason = 'Recognized a cantrip list from the spellcasting text.';
      } else if (/\bprepared spells?\b/.test(combined)) {
        friendlyName = 'Prepared spells';
        nextSuggestedType = 'spells';
        extraTags.add('spells');
        suggestedReason = 'Recognized a prepared-spell list from the pasted text.';
      } else if (/\bspells? known\b/.test(combined)) {
        friendlyName = 'Known spells';
        nextSuggestedType = 'spells';
        extraTags.add('spells');
        suggestedReason = 'Recognized a spell list from the pasted text.';
      } else if (/^ability scores?(?: and abilities)?$/.test(normalizedName) || (detectedAbilities.length > 1 && (hasAbilityChecks || hasSavingThrows || hasSkillSignals))) {
        friendlyName = 'Ability scores and related checks';
        nextSuggestedType = 'abilities';
        extraTags.add('abilities');
        if (hasSavingThrows) extraTags.add('savingThrows');
        if (hasSkillSignals) extraTags.add('skills');
        suggestedReason = 'Recognized multiple ability scores together with related saving throws, checks, or skills.';
      } else if (detectedAbilities.length === 1 && (hasAbilityChecks || hasSavingThrows || hasSkillSignals)) {
        friendlyName = `${detectedAbilities[0].label} and related checks`;
        nextSuggestedType = 'abilities';
        extraTags.add('abilities');
        if (hasSavingThrows) extraTags.add('savingThrows');
        if (hasSkillSignals) extraTags.add('skills');
        suggestedReason = 'Recognized an ability score block with related saving throws, checks, or skills.';
      } else if ((normalizedName === 'untitled block' || normalizedName === 'unplaced details') && hasSavingThrows) {
        friendlyName = 'Saving throws';
        nextSuggestedType = 'savingThrows';
        extraTags.add('savingThrows');
        suggestedReason = 'Recognized a saving throw bundle from the pasted text.';
      } else if ((normalizedName === 'untitled block' || normalizedName === 'unplaced details') && detectedSkills.length >= 2) {
        friendlyName = 'Skills and proficiencies';
        nextSuggestedType = 'skills';
        extraTags.add('skills');
        suggestedReason = 'Recognized a skill bundle from the pasted text.';
      } else if (!rawName || normalizedName === 'untitled block') {
        friendlyName = 'Unplaced details';
      }

      return {
        friendlyName,
        suggestedType: nextSuggestedType,
        extraTags: Array.from(extraTags),
        suggestedReason
      };
    };

    // @date_of_change 2025-12-09 - Lightweight tagging for later UI grouping
    const classifyMeta = (name, description, suggestedType) => {
      const lowerName = (name || '').toLowerCase();
      const lowerDesc = (description || '').toLowerCase();
      const combined = `${lowerName} ${lowerDesc}`;

      let source = null;
      let subType = null;
      const tags = new Set();

      if (suggestedType) {
        tags.add(suggestedType);
      }

      if (/from spell storing item/.test(lowerDesc) || /spell storing item/.test(lowerName)) {
        source = 'spellStoringItem';
        tags.add('equipment');
        tags.add('spellContainer');
      } else if (/from scrolls?/.test(lowerDesc) || /^scrolls?$/.test(lowerName) || /\bspell scroll\b/.test(combined) || /\bscroll of\b/.test(combined)) {
        source = source || 'scrolls';
        tags.add('equipment');
        tags.add('scroll');
      }

      if (/bloodforged|bloodforge/.test(combined)) {
        source = source || 'bloodforged';
        tags.add('equipment');
        tags.add('bloodforged');
      }

      if (/regeneration|sunlight weakness|undead resilience|spider climb|insatiable thirst|fear of divine wrath|unnatural sight|children of the night/i.test(name + ' ' + description)) {
        subType = 'speciesTrait';
        tags.add('traits');
        tags.add('vampiric');
      }

      if (/multiattack|legendary actions?|legendary resistance|lair actions?|bite|claw/.test(lowerName)) {
        subType = subType || 'monsterAction';
        tags.add('actions');
      }

      if (/potion|elixir|vial|phial|draught|consumable|oil|salve|dust|powder|bead of/i.test(combined)) {
        tags.add('consumable');
      }

      if (/boots?|sandals?|shoes|cloak|robe|armor|mail|plate|leather|ring|amulet|necklace|helm|helmet|mask|gloves?|gauntlets?|belt|bracers?|shield/i.test(combined)) {
        tags.add('worn');
      }

      if (/weapon|sword|blade|axe|bow|dagger|mace|staff|wand|rod|crossbow|hammer|spear|whip|flail|scimitar|rapier|glaive|halberd|pike/i.test(combined)) {
        tags.add('weaponLike');
      }

      return { source, subType, tags: Array.from(tags) };
    };

    const seenKeys = new Set();
    const items = [];
    let dedupedAgainstCatalog = 0;

    const pushItemIfUnique = (base, keyPrefix, index, suggestedType) => {
      const name = base.name || '';
      const description = base.description || base.rawLine || '';
      const normName = this.normalizeValue(name);
      if (!normName) return;

      // Skip structural labels when their canonical fields are already populated
      if (isStructuralAndAlreadyParsed(normName)) return;
      if (this.looksLikeStructuredDuplicateBlock(base, data)) return;

      if (this.isDuplicateAgainstCatalog(base, suggestedType, catalogIndex)) {
        dedupedAgainstCatalog += 1;
        return;
      }

      const key = `${keyPrefix}-${index}`;
      if (seenKeys.has(key)) return;

      const inferredPresentation = inferReviewPresentation(name, description, suggestedType);
      const resolvedName = inferredPresentation.friendlyName || name;
      const resolvedSuggestedType = inferredPresentation.suggestedType || suggestedType;
      const meta = classifyMeta(resolvedName, description, resolvedSuggestedType);
      const parserMeta = base.parserMeta && typeof base.parserMeta === 'object' ? { ...base.parserMeta } : null;
      const sourceLabel = this.formatSourceSection(parserMeta?.sourceSection);
      const relatedCatalogEntries = this.findRelatedCatalogEntries(base, resolvedSuggestedType, catalogIndex);
      const mergedTags = Array.from(new Set([
        ...(Array.isArray(base.tags) ? base.tags : []),
        ...(Array.isArray(meta.tags) ? meta.tags : []),
        ...(Array.isArray(inferredPresentation.extraTags) ? inferredPresentation.extraTags : [])
      ]));

      items.push({
        ...base,
        key,
        name: resolvedName,
        description,
        suggestedType: resolvedSuggestedType,
        parserMeta,
        parseConfidence: typeof parserMeta?.parseConfidence === 'number' ? parserMeta.parseConfidence : null,
        sourceLabel,
        reviewReason: parserMeta?.reviewReason || inferredPresentation.suggestedReason || null,
        relatedCatalogEntries,
        source: meta.source || base.source || null,
        subType: meta.subType || base.subType || null,
        tags: mergedTags
      });
      seenKeys.add(key);
    };

    potentialEquipment.forEach((eq, idx) => pushItemIfUnique(eq, 'equip', idx, 'equipment'));

    potentialFeatures.forEach((feat, idx) => pushItemIfUnique(feat, 'feature', idx, 'features'));

    potentialTraits.forEach((trait, idx) => pushItemIfUnique(trait, 'trait', idx, 'traits'));

    // date_of_change: 2025-12-12
    // rule_reference: Include combat attacks (Bite, Claw, Force Strike) as actions
    potentialActions.forEach((action, idx) => pushItemIfUnique(action, 'action', idx, 'actions'));

    sectionFragments.forEach((fragment, idx) => {
      const fragmentSection = fragment.category || fragment?.parserMeta?.sourceSection || 'section';
      const label = this.formatSourceSection(fragmentSection) || 'Section';
      const description = Array.isArray(fragment.lines)
        ? fragment.lines.join(' ')
        : String(fragment.lines || '');
      if (!description.trim()) return;

      pushItemIfUnique(
        {
          name: `${label} leftovers`,
          description,
          isSectionFragment: true,
          fragmentCategory: fragmentSection,
          fragmentHeader: fragment.header || null,
          lines: Array.isArray(fragment.lines) ? [...fragment.lines] : [],
          parserMeta: fragment.parserMeta
        },
        'fragment',
        idx,
        'notes'
      );
    });

    rawTextBlocks.forEach((block, idx) => {
      const header = block.header || 'Untitled Block';
      const description = Array.isArray(block.lines)
        ? block.lines.join(' ')
        : String(block.lines || '');
      const normalizedHeader = this.normalizeValue(header);
      const handledSectionHeaders = new Set([
        'passive triggered features',
        'passive / triggered features',
        'abilities traits',
        'abilities and traits',
        'features',
        'feats',
        'species traits'
      ]);
      const hasFeatureLikeCatalogEntries =
        (catalogIndex.kindCounts[this.normalizeValue('features')] || 0) > 0 ||
        (catalogIndex.kindCounts[this.normalizeValue('classFeatures')] || 0) > 0 ||
        (catalogIndex.kindCounts[this.normalizeValue('feats')] || 0) > 0 ||
        (catalogIndex.kindCounts[this.normalizeValue('speciesTraits')] || 0) > 0;
      if (handledSectionHeaders.has(normalizedHeader) && hasFeatureLikeCatalogEntries) {
        return;
      }
      const norm = this.normalizeValue(description) || this.normalizeValue(header);
      if (!norm) return;
      pushItemIfUnique(
        {
          name: header,
          description,
          isRaw: true,
          parserMeta: block.parserMeta
        },
        'block',
        idx,
        'notes'
      );
    });

    const byCategory = items.reduce((acc, item) => {
      const cat = item.suggestedType || 'unknown';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});

    return {
      items,
      byCategory,
      stats: {
        totalOriginal,
        totalAfterDedup: items.length,
        dedupedAgainstCatalog
      }
    };
  }
}

export default SmartPasteAggregator;
